import {
  LitElement,
  html,
  css,
  classMap,
  broadcastReceiver,
} from '@hh/global.js'

class HHMobileMenu extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    activeFirstLevel: {
      type: String,
    },
  }

  constructor() {
    super()

    this.classes = {
      active: 'active',
    }
  }

  /* getters */
  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get announcementBar() {
    return document.querySelector('.js-announcement-bar-section') ?? null
  }

  get menuOpener() {
    return document.getElementById('mobile-menu-opener') ?? null
  }

  get menu() {
    return this.shadowRoot.querySelector('.menu') ?? null
  }

  get firstLevel() {
    const slot = this.shadowRoot?.querySelector(`slot[name='first-level']`)
    return slot?.assignedNodes()[0] ?? null
  }

  get firstLevelButtons() {
    return this.firstLevel?.querySelectorAll('button') ?? []
  }

  get secondLevels() {
    const slot = this.shadowRoot?.querySelector(`slot[name='second-level']`)
    return slot?.assignedNodes() ?? []
  }

  get secondLevelsButtons() {
    return this.secondLevels
      .map((slot) => [...slot.querySelectorAll('button')])
      .flat()
  }

  get thirdLevels() {
    const slot = this.shadowRoot?.querySelector(`slot[name='third-level']`)
    return slot?.assignedNodes() ?? []
  }

  connectedCallback() {
    super.connectedCallback()

    this.openReceiver = broadcastReceiver(
      'mobile-menu:open',
      this.openFromDocument.bind(this)
    )
    this.closeReceiver = broadcastReceiver(
      'mobile-menu:close',
      this.closeMenuClicked.bind(this)
    )

    window.addEventListener('search:opened', this.closeFromDocument.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.openReceiver.disconnect()
    this.closeReceiver.disconnect()
    window.removeEventListener('search:opened', this.closeFromDocument)
  }

  firstUpdated() {
    this.setDefaultFirstLevel()
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      if (this.open) {
        this.emiMobileMenuOpenEvent()
        document.documentElement.classList.add(
          'overflow-hidden',
          'mobile-menu-open'
        )
      } else {
        document.documentElement.classList.remove(
          'overflow-hidden',
          'mobile-menu-open'
        )
      }
    }

    if (changedProperties.has('activeFirstLevel')) {
      this.updateSecondLevel(this.activeFirstLevel)
    }
  }

  async openFromDocument() {
    await this.updateHeaderHeight()
    this.open = true
    this.menuOpener?.setAttribute('aria-expanded', 'true')
    document.body.classList.add('mobile-menu-open')

    this.firstLevel.setAttribute('tabindex', '0')
    setTimeout(() => {
      this.firstLevel.focus()
      this.firstLevel.removeAttribute('tabindex')
    }, 0)
  }

  closeFromDocument() {
    this.open = false
    document.body.classList.remove('mobile-menu-open')
    this.menuOpener?.setAttribute('aria-expanded', 'false')
    setTimeout(() => {
      this.setDefaultFirstLevel()
      this.removeThirdLevelActive()
    }, 800)
  }

  closeMenuClicked() {
    const expandedFirstLevelItem = document.querySelector('.mobile-menu__first-level-button[aria-expanded="true"]');
    const expandedSecondLevelItem = document.querySelector('.mobile-menu__second-level-button[aria-expanded="true"]');

    if (expandedFirstLevelItem !== null && expandedSecondLevelItem !== null) {
      expandedFirstLevelItem.click();
    } else {
      this.closeFromDocument();
    }
  }

  emiMobileMenuOpenEvent() {
    this.dispatchEvent(
      new CustomEvent('mobile-menu:opened', {
        bubbles: true,
        composed: true,
      })
    )
  }

  updateHeaderHeight() {
    return new Promise((resolve) => {
      this.headerHeight = this.header.offsetHeight
      this.announcementBarHeight = this.announcementBar?.offsetHeight || 0
      this.totalHeaderHeight = this.headerHeight + this.announcementBarHeight

      HH.setCSSVariable(
        '--hh-total-header-height',
        `${this.totalHeaderHeight}px`
      )
      resolve()
    })
  }

  onFirstLevelClick(e) {
    e.preventDefault()
    const { target } = e
    const { menu } = target.dataset
    this.firstLevelButtons.forEach((button) => {
      button.classList.remove(this.classes.active)
      button.setAttribute('aria-expanded', 'false')
    })
    target.classList.add(this.classes.active)
    target.setAttribute('aria-expanded', 'true')
    this.activeFirstLevel = menu
    this.removeThirdLevelActive()
  }

  setDefaultFirstLevel() {
    const activeMenu = this.firstLevelButtons[0]
    activeMenu.click()
  }

  updateSecondLevel(menu) {
    this.secondLevels.forEach((secondLevel) =>
      secondLevel.classList.remove(this.classes.active)
    )
    const activeMenu = this.secondLevels.find(
      (secondLevel) => secondLevel.dataset.menu === menu
    )
    activeMenu.classList.add(this.classes.active)
  }

  onSecondLevelClick(e) {
    const { target } = e
    const { menu } = target.dataset
    if (menu) {
      e.preventDefault()

      this.thirdLevels.forEach((thirdLevel) =>
        thirdLevel.classList.remove(this.classes.active)
      )
      const activeMenu = this.thirdLevels.find(
        (thirdLevel) => thirdLevel.dataset.menu === menu
      )
      activeMenu.classList.add(this.classes.active)
      activeMenu.removeAttribute('inert')
      const firstFocusable = activeMenu.querySelector('a')
      firstFocusable.focus()
      target.setAttribute('aria-expanded', 'true')
    }
  }

  onThirdLevelClick(e) {
    const { target } = e
    const { menu } = target.dataset
    if (menu) {
      e.preventDefault()
      this.removeThirdLevelActive()
    }
  }

  removeThirdLevelActive() {
    this.thirdLevels.forEach((thirdLevel) => {
      thirdLevel.classList.remove(this.classes.active)
      thirdLevel.setAttribute('inert', '')
      this.secondLevelsButtons.forEach((button) =>
        button.setAttribute('aria-expanded', 'false')
      )
    })
  }

  render() {
    return html`
      <div
        class=${classMap({
          menu: true,
          active: this.open,
        })}
        ?inert=${!this.open}
      >
        <div class="menu-inner">
          <div class="first-level">
            <slot name="first-level" @click=${this.onFirstLevelClick}></slot>
          </div>
          <div class="second-level">
            <slot name="second-level" @click=${this.onSecondLevelClick}></slot>
          </div>
          <div class="third-level">
            <slot name="third-level" @click=${this.onThirdLevelClick}></slot>
          </div>
        </div>
      </div>
    `
  }

  static styles = css`
    @media (min-width: 1024px) {
      :host {
        display: none;
      }
    }

    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .menu {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      transition: transform var(--hh-duration-x-long),
        top var(--hh-duration-medium);
      transform: translateY(-100%);
      width: 100%;
      z-index: 99;
    }

    .menu.active {
      transform: translateY(0);
      top: var(--hh-total-header-height, 0);
    }

    .menu-inner {
      width: 100%;
      height: 100%;
      background-color: var(--hh-color-day);
      display: flex;
      flex-direction: column;
    }

    ::slotted([slot='first-level']) {
      display: flex;
      justify-content: center;
    }

    ::slotted([slot='first-level']:focus) {
      outline: none !important;
    }

    .first-level {
      flex: 0 0 auto;
      padding: 0 var(--hh-spacing-xl);
      margin: 0 -0.6rem var(--hh-spacing-lg) -0.6rem;
    }

    .second-level {
      flex: 1 1 auto;
      padding: 0 var(--hh-spacing-xl) var(--hh-spacing-xl);
      overflow-y: auto;
      height: 100%;
    }

    .second-level::-webkit-scrollbar {
      width: 0.2rem;
    }
    .second-level::-webkit-scrollbar-thumb {
      width: 0.2rem;
      background: var(--hh-color-brand-primary);
    }
  `
}

customElements.define('hh-mobile-menu', HHMobileMenu)
